import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import offlineSyncService from '../../../services/offlineSyncService';
import offlineStorageService from '../../../services/offlineStorageService';
import { useAuth } from '../../../contexts/AuthContext';
import appointmentService from '../../../services/appointmentService';

/**
 * Completion Screen Component
 * Shows after successful check-in submission
 * Displays sync status for offline entries
 */
const CompletionScreen = ({ entryData, entryId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [nearestAppointment, setNearestAppointment] = useState(null);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  useEffect(() => {
    updatePendingCount();
    if (user?.id) {
      appointmentService?.getNearestAppointment(user?.id)?.then(appt => setNearestAppointment(appt));
    }
    
    // Monitor connectivity
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Subscribe to sync events
    const unsubscribe = offlineSyncService?.onSyncEvent((event) => {
      if (event?.type === 'sync_completed') {
        updatePendingCount();
      }
    });
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
    };
  }, [user]);

  const updatePendingCount = async () => {
    if (user?.id) {
      const count = await offlineStorageService?.getPendingCount(user?.id);
      setPendingCount(count);
    }
  };

  const handleViewHistory = () => {
    navigate('/symptom-history');
  };

  const handleNewCheckIn = () => {
    window.location?.reload();
  };

  const handleDashboard = () => {
    navigate('/caregiver-dashboard');
  };

  const handleExportToDoctor = () => {
    // Navigate to Doctor Summary Export with pre-populated data
    navigate('/doctor-summary-export', {
      state: {
        prePopulatedEntry: entryData,
        entryId: entryId,
        fromCheckIn: true
      }
    });
  };

  const handleViewAppointments = () => {
    navigate('/doctor-appointments');
  };

  const isOfflineEntry = entryData?.savedOffline || false;

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center py-12 px-4">
      {/* Success Icon */}
      <div className="inline-flex items-center justify-center w-24 h-24 bg-success/10 rounded-full mb-6 animate-in zoom-in duration-500">
        <Icon name="CheckCircle2" size={56} color="var(--color-success)" strokeWidth={2} />
      </div>
      {/* Title */}
      <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4 text-center">
        Check-in Complete!
      </h2>
      {/* Description */}
      <p className="text-base md:text-lg text-muted-foreground text-center max-w-md mb-6">
        {isOfflineEntry 
          ? 'Your symptoms have been saved offline and will sync automatically when connection is restored.' :'Your symptoms have been recorded successfully. Keep tracking your health journey!'
        }
      </p>
      {/* Offline Status Card */}
      {isOfflineEntry && (
        <div className="w-full max-w-md mb-6">
          <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="WifiOff" size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-warning-foreground mb-1">
                  Saved Offline
                </h3>
                <p className="text-xs text-warning-foreground/80 mb-2">
                  This check-in is stored locally on your device. It will automatically sync to the cloud when your internet connection is restored.
                </p>
                {pendingCount > 1 && (
                  <p className="text-xs text-warning-foreground/80">
                    You have <strong>{pendingCount} check-ins</strong> waiting to sync.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Online with pending syncs */}
      {!isOffline && pendingCount > 0 && (
        <div className="w-full max-w-md mb-6">
          <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="RefreshCw" size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-foreground mb-1">
                  Syncing in Progress
                </h3>
                <p className="text-xs text-muted-foreground">
                  {pendingCount} check-in{pendingCount > 1 ? 's are' : ' is'} being synced to the cloud.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Appointment Banner */}
      {nearestAppointment && (() => {
        const apptDate = new Date(nearestAppointment?.appointment_date);
        const diffDays = Math.ceil((apptDate?.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        return (
          <div className="w-full max-w-md mb-4">
            <div
              className="p-3 bg-primary/10 border border-primary/20 rounded-lg cursor-pointer hover:bg-primary/15 transition-colors"
              onClick={handleViewAppointments}
            >
              <div className="flex items-center gap-2">
                <Icon name="Stethoscope" size={16} className="text-primary flex-shrink-0" />
                <p className="text-xs text-foreground">
                  <span className="font-medium">Appointment with {nearestAppointment?.doctor_name}</span>
                  {' '}in <span className="font-semibold text-primary">{diffDays} day{diffDays !== 1 ? 's' : ''}</span> — keep tracking!
                </p>
                <Icon name="ChevronRight" size={14} className="text-primary ml-auto flex-shrink-0" />
              </div>
            </div>
          </div>
        );
      })()}
      {/* Entry Summary */}
      <div className="w-full max-w-md mb-8 p-4 bg-card border border-border rounded-lg">
        <h3 className="text-sm font-semibold text-foreground mb-3">Check-in Summary</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Feeling Level:</span>
            <span className="font-medium text-foreground capitalize">
              {entryData?.feelingLevel || 'Not specified'}
            </span>
          </div>
          {entryData?.symptomCategories?.length > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Symptoms:</span>
              <span className="font-medium text-foreground">
                {entryData?.symptomCategories?.length} recorded
              </span>
            </div>
          )}
          {entryData?.severity && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Severity:</span>
              <span className="font-medium text-foreground">
                {entryData?.severity}/10
              </span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Recorded:</span>
            <span className="font-medium text-foreground">
              {new Date(entryData?.timestamp)?.toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="w-full max-w-md space-y-3">
        <Button
          variant="default"
          size="lg"
          onClick={handleExportToDoctor}
          iconName="FileText"
          iconPosition="left"
          className="w-full"
        >
          Export to Doctor
        </Button>

        <Button
          variant="outline"
          size="lg"
          onClick={handleViewAppointments}
          iconName="Stethoscope"
          iconPosition="left"
          className="w-full"
        >
          My Appointments
        </Button>
        
        <Button
          variant="outline"
          size="lg"
          onClick={handleViewHistory}
          iconName="History"
          iconPosition="left"
          className="w-full"
        >
          View History
        </Button>
        
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            size="lg"
            onClick={handleNewCheckIn}
            iconName="Plus"
            iconPosition="left"
          >
            New Check-in
          </Button>
          
          <Button
            variant="outline"
            size="lg"
            onClick={handleDashboard}
            iconName="LayoutDashboard"
            iconPosition="left"
          >
            Dashboard
          </Button>
        </div>
      </div>
      {/* Info Note */}
      <div className="mt-8 max-w-md">
        <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
          <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">
            Your health data is encrypted and securely stored. {isOfflineEntry ? 'Offline entries are protected on your device until synced.' : 'You can access your history anytime from the dashboard.'}
          </p>
        </div>
      </div>
      {/* Feedback Prompt */}
      <div className="mt-8 w-full max-w-md">
        <div className="p-4 bg-accent/5 border border-accent/20 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="MessageSquare" size={18} className="text-accent flex-shrink-0" />
            <h3 className="text-sm font-semibold text-foreground">Share your feedback</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            You've just used the app — we'd love to hear what you think! Help us shape MyBodyToday to better serve your needs.
          </p>
          <button
            onClick={() => setFeedbackModalOpen(true)}
            className="text-xs font-semibold text-accent hover:underline flex items-center gap-1"
          >
            <Icon name="MessageSquare" size={14} className="text-accent" />
            Open feedback form
          </button>
        </div>
      </div>

      {/* Feedback Modal */}
      {feedbackModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Icon name="MessageSquare" size={20} className="text-accent" />
                <h2 className="text-base font-semibold text-foreground">Share Your Feedback</h2>
              </div>
              <button
                onClick={() => setFeedbackModalOpen(false)}
                className="p-1.5 rounded-full hover:bg-muted transition-colors"
                aria-label="Close feedback form"
              >
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>
            {/* Modal Body */}
            <div className="flex-1 overflow-auto">
              <iframe
                src="https://docs.google.com/forms/d/e/1FAIpQLSe6DxNnu3C_BHnd_36p7VJBmgRgu8Qc9bdBILehwX_qztMAow/viewform?embedded=true"
                width="100%"
                height="700"
                frameBorder="0"
                marginHeight={0}
                marginWidth={0}
                title="MyBodyToday Feedback Form"
                className="w-full"
                style={{ minHeight: '700px' }}>
                Loading feedback form…
              </iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompletionScreen;